import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, tap } from 'rxjs';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { AssistantBuilderPage } from './assistant-builder/assistant-builder.page';
import { AssistantBuilderModalActions } from './store/assistants.actions';
import { assistantsFeature, AssistantsState } from './store/assistants.reducer';

/**
 * The skill builder overlay and everything that guards dismissing it, in one
 * place so a second route can render it without restating any of it
 * (SB-3650 / T5, KD-32 on the map — squadbox-harness#7).
 *
 * PRESENTATIONAL AND STORE-DRIVEN, with no inputs: the whole contract is the
 * `builderModal` slice, exactly as `KnowledgeModalComponent` reads the
 * `knowledgeModal` one. That is what lets the Teammate page render this element
 * with one line of template.
 *
 * It owns FOUR things that were previously bolted to `assistants.page.ts` and
 * would otherwise have to be written twice:
 *   1. the `@if` over the slice, which destroys the builder on close;
 *   2. `builderAssistant()`, the snapshot binding — see its own doc comment;
 *   3. `canDeactivate()`, the in-app navigate-away prompt;
 *   4. the `beforeunload` listener, the refresh / tab-close prompt.
 *
 * (3) and (4) are ONE protection with two triggers. Moving either without the
 * other ships a builder that prompts on navigation and discards unsaved work
 * silently on refresh, which is why they live together rather than in a service
 * — a `@HostListener` needs a component host.
 *
 * It owns the BUILDER's lifecycle only. The knowledge modal also outlives its
 * page — both slices sit on the `/chats` parent — but closing it from
 * `canDeactivate` is wrong: that hook also runs on a same-route PARAM change
 * (both routes set `runGuardsAndResolvers: 'paramsOrQueryParamsChange'`), and a
 * background `loadAssistants` can trigger one on its own, which would orphan
 * the user's staged S3 uploads with no prompt. Tracked as squadbox-app#1509.
 *
 * NOT exported from `./index.ts`, deliberately: it imports `AssistantBuilderPage`,
 * which is the edge that barrel keeps out (see the comment there).
 */
@Component({
  selector: 'squadbox-skill-builder-host',
  imports: [AssistantBuilderPage],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (builderModal().kind !== 'closed') {
      <squadbox-assistant-builder
        [assistant]="builderAssistant()"
        [routeMode]="builderModal().routeMode"
      />
    }
  `,
})
export class SkillBuilderHostComponent {
  private readonly assistantsStore = inject<Store<AssistantsState>>(Store);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);

  /** Skill-builder modal state (SB-3325 PR-4, Stage B). */
  public readonly builderModal = this.assistantsStore.selectSignal(
    assistantsFeature.selectBuilderModal,
  );

  /**
   * The AssistantView being edited, or undefined on create.
   *
   * Reads the one-time snapshot captured in the slice at `openEdit` — NOT a live
   * entity lookup. A live read would let a background `loadAssistants` reload
   * (`setAll`, new refs) re-fire the builder's patch/reset/share-seed effects
   * mid-edit (data loss), or drop the entity so this returns undefined and Save
   * falls onto the CREATE branch (duplicate). The snapshot keeps `[assistant]`
   * stable and defined for the modal's lifetime.
   */
  public readonly builderAssistant = computed(() => {
    const modal = this.builderModal();
    return modal.kind === 'edit' ? (modal.assistant ?? undefined) : undefined;
  });

  private readonly builderRef = viewChild(AssistantBuilderPage);

  /**
   * Browser refresh / tab close can't render a custom modal, so fall back to the
   * native prompt while the open builder holds unsaved work.
   */
  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (
      this.builderModal().kind !== 'closed' &&
      this.builderRef()?.hasChanges()
    ) {
      event.preventDefault();
      // Older browsers only raise the native confirmation when `returnValue`
      // is set; modern engines honor `preventDefault()` alone. Set both so the
      // unsaved-work prompt fires everywhere (the empty string is intentional —
      // the browser substitutes its own generic message). `returnValue` is
      // spec-deprecated but remains the required cross-browser trigger here.
      // eslint-disable-next-line sonarjs/deprecation
      event.returnValue = '';
    }
  }

  /**
   * CanDeactivate hook: prompt before navigating away while the open builder
   * holds unsaved work; otherwise navigate freely. Whenever navigation is
   * allowed (Discard, nothing to discard, or already closed), dispatch `close()`
   * so the surviving parent-route slice cannot re-open it and any pending
   * `openManagedEdit` is cancelled too.
   *
   * The hosting page delegates to this; the route guard delegates to the page.
   */
  public canDeactivate(): Observable<boolean> {
    const builder = this.builderRef();
    const isModalOpen = this.builderModal().kind !== 'closed';
    if (!isModalOpen || !builder?.hasChanges()) {
      this.assistantsStore.dispatch(AssistantBuilderModalActions.close());
      return of(true);
    }
    return this.confirmDialog
      .confirm({
        title: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.TITLE',
        ) as string,
        message: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.DESCRIPTION',
        ) as string,
        confirmLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_DISCARD',
        ) as string,
        cancelLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_CANCEL',
        ) as string,
        confirmType: 'delete',
      })
      .pipe(
        tap((confirmed) => {
          if (confirmed) {
            this.assistantsStore.dispatch(AssistantBuilderModalActions.close());
          }
        }),
      );
  }
}
